import React, { useState, useEffect } from 'react';
import { Plus, X, FileText, AlertCircle, Check, Edit, Activity, ChevronDown, ChevronUp, Filter } from 'lucide-react';

// Importando datos desde el archivo de mocks
import { 
  mockSnomed, diagnosisDatabase, mockPatientHistory,
  relatedSymptomsMap, contradictionRules, severityMap, badgeColorMap
} from '../../mock/mocksSintomasDiagnosticos';


export default function SymptomsAndFindings({ onDiagnosisSelect, onDiagnosisStatusChange }) {
  // Estados
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [dynamicDiagnoses, setDynamicDiagnoses] = useState([]);
  const [filteredDiagnoses, setFilteredDiagnoses] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentSymptom, setCurrentSymptom] = useState(null);
  const [symptomNotes, setSymptomNotes] = useState('');
  const [showPatientHistory, setShowPatientHistory] = useState(false);
  const [contradictions, setContradictions] = useState([]);
  const [relatedSymptomsVisible, setRelatedSymptomsVisible] = useState(true);
  const [expandedDiagnoses, setExpandedDiagnoses] = useState([]);

  
  // Nuevos estados para filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    specialty: null,
    bodySystem: null,
    minimumProbability: 0,
    showOnlySevere: false
  });
  
  // Extraer especialidades y sistemas corporales únicos para los selectores
  const specialties = [...new Set(diagnosisDatabase.flatMap(d => d.specialty))].sort();
  const bodySystems = [...new Set(diagnosisDatabase.map(d => d.bodySystem))].sort();

  // Toggle diagnóstico expandido
  const toggleDiagnosisExpand = (index) => {
    if (expandedDiagnoses.includes(index)) {
      setExpandedDiagnoses(expandedDiagnoses.filter(i => i !== index));
    } else {
      setExpandedDiagnoses([...expandedDiagnoses, index]);
    }
  };

  // Búsqueda de síntomas
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }
    
    const results = mockSnomed.filter(item => 
      item.term.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !selectedSymptoms.some(s => s.id === item.id)
    );
    setSearchResults(results);
  }, [searchTerm, selectedSymptoms]);

  // Cálculo de diagnósticos
  useEffect(() => {
    if (selectedSymptoms.length === 0) {
      setDynamicDiagnoses([]);
      return;
    }

    const calculatedDiagnoses = diagnosisDatabase.map(diagnosis => {
      let probability = diagnosis.baseProbability;
      let matchingSymptoms = 0;
      let supportingFactors = [];
      let contradictingFactors = [];

      diagnosis.associatedSymptoms.forEach(associatedSymptom => {
        const selectedSymptom = selectedSymptoms.find(s => s.term === associatedSymptom.symptomTerm);
        
        if (selectedSymptom) {
          const severityKey = selectedSymptom.severity === "present" ? "mild" : selectedSymptom.severity;
          
          if (associatedSymptom.requiredSeverity.includes(severityKey)) {
            probability += associatedSymptom.weight;
            matchingSymptoms++;
            supportingFactors.push({
              factor: selectedSymptom.term,
              status: "presente",
              severity: selectedSymptom.severity,
              importance: associatedSymptom.weight > 25 ? "high" : associatedSymptom.weight > 15 ? "medium" : "low"
            });
          } else {
            contradictingFactors.push({
  factor: selectedSymptom.term,
  status: `Severidad ${severityMap[selectedSymptom.severity]} (se esperaba ${associatedSymptom.requiredSeverity.map(s => severityMap[s] || s).join(' o ')})`,
  importance: "medium"
});
          }
        } else if (associatedSymptom.weight > 20) {
          contradictingFactors.push({
            factor: associatedSymptom.symptomTerm,
            status: "no presente",
            importance: associatedSymptom.weight > 30 ? "high" : "medium"
          });
        }
      });

      if (matchingSymptoms > 1) probability += matchingSymptoms * 5;
      probability = Math.max(0, Math.min(100, probability));

      return {
        ...diagnosis,
        probability: Math.round(probability),
        supportingFactors,
        contradictingFactors,
        status: null
      };
    })
    .filter(diagnosis => diagnosis.probability > 5)
    .sort((a, b) => b.probability - a.probability);

    setDynamicDiagnoses(calculatedDiagnoses);
    checkContradictions(selectedSymptoms);
  }, [selectedSymptoms]);

  // Aplicar filtros a los diagnósticos
  useEffect(() => {
    let filtered = [...dynamicDiagnoses];
    
    if (filters.specialty) {
      filtered = filtered.filter(d => d.specialty.includes(filters.specialty));
    }
    
    if (filters.bodySystem) {
      filtered = filtered.filter(d => d.bodySystem === filters.bodySystem);
    }
    
    if (filters.minimumProbability > 0) {
      filtered = filtered.filter(d => d.probability >= filters.minimumProbability);
    }
    
    if (filters.showOnlySevere) {
      filtered = filtered.filter(d => d.severity === "alta");
    }
    
    setFilteredDiagnoses(filtered);
  }, [dynamicDiagnoses, filters]);

  // Verificar contradicciones
  const checkContradictions = (symptoms) => {
    const foundContradictions = [];
    
    contradictionRules.forEach(rule => {
      if (rule.symptoms) {
        const foundSymptomTerms = symptoms.map(s => s.term);
        if (rule.symptoms.every(term => foundSymptomTerms.includes(term))) {
          foundContradictions.push(rule.message);
        }
      } else if (rule.condition && rule.condition(symptoms)) {
        foundContradictions.push(rule.message);
      }
    });
    
    setContradictions(foundContradictions);
  };

  // Obtener síntomas relacionados
  const getRelatedSymptoms = () => {
    const related = new Set();
    selectedSymptoms.forEach(symptom => {
      const relatedForSymptom = relatedSymptomsMap[symptom.term] || [];
      relatedForSymptom.forEach(term => {
        if (!selectedSymptoms.some(s => s.term === term)) related.add(term);
      });
    });
    return Array.from(related).map(term => {
      const symptom = mockSnomed.find(s => s.term === term);
      return symptom || { id: term, code: "", term: term };
    });
  };

  // Agregar síntoma
  const addSymptom = (symptom) => {
    const newSymptom = {
      ...symptom,
      timestamp: new Date().toISOString(),
      severity: "present",
      notes: "",
      source: "doctor",
      status: "present", 
      imported: symptom.source === "previous"
    };
    
    setSelectedSymptoms([...selectedSymptoms, newSymptom]);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Gestión de síntomas y diagnósticos
  const removeSymptom = (symptomId) => setSelectedSymptoms(selectedSymptoms.filter(s => s.id !== symptomId));

  const updateSeverity = (symptomId, severityLevel) => {
    const mappedSeverity = severityLevel === 1 ? "mild" : 
                         severityLevel === 2 ? "moderate" : 
                         severityLevel === 3 ? "severe" : "present";
    
    setSelectedSymptoms(selectedSymptoms.map(s => 
      s.id === symptomId ? { ...s, severity: mappedSeverity } : s
    ));
  };

  const importFromHistory = (consultationSymptoms) => {
    const newSymptoms = [...selectedSymptoms];
    consultationSymptoms.forEach(historicalSymptom => {
      if (!selectedSymptoms.some(s => s.id === historicalSymptom.id)) {
        newSymptoms.push({...historicalSymptom, timestamp: new Date().toISOString(), imported: true});
      }
    });
    setSelectedSymptoms(newSymptoms);
    setShowPatientHistory(false);
  };

  const openNotesModal = (symptom) => {
    setCurrentSymptom(symptom);
    setSymptomNotes(symptom?.notes || '');
    setShowNotesModal(true);
  };

  const saveNotes = () => {
    if (!currentSymptom) return;
    setSelectedSymptoms(selectedSymptoms.map(s => 
      s.id === currentSymptom.id ? { ...s, notes: symptomNotes } : s
    ));
    setShowNotesModal(false);
  };

  // FUNCIONES MODIFICADAS: Alternar estado de diagnósticos y notificar al padre
  const toggleConfirmDiagnosis = (index) => {
    const updatedDiagnoses = [...dynamicDiagnoses];
    const diagnosis = updatedDiagnoses[index];
    
    // Si ya está confirmado, lo deseleccionamos (status null)
    // Si no está confirmado o está descartado, lo confirmamos
    const newStatus = diagnosis.status === 'confirmed' ? null : 'confirmed';
    updatedDiagnoses[index].status = newStatus;
    
    setDynamicDiagnoses(updatedDiagnoses);
    
    // Notificar al componente padre
    if (onDiagnosisStatusChange) {
      onDiagnosisStatusChange({
        diagnosisName: diagnosis.term,
        diagnosisId: diagnosis.id,
        status: newStatus,
        probability: diagnosis.probability,
        severity: diagnosis.severity,
        specialty: diagnosis.specialty,
        bodySystem: diagnosis.bodySystem,
        description: diagnosis.description,
        supportingFactors: diagnosis.supportingFactors,
        contradictingFactors: diagnosis.contradictingFactors,
        timestamp: new Date().toISOString()
      });
    }
  };

  const toggleDiscardDiagnosis = (index) => {
    const updatedDiagnoses = [...dynamicDiagnoses];
    const diagnosis = updatedDiagnoses[index];
    
    // Si ya está descartado, lo deseleccionamos (status null)
    // Si no está descartado o está confirmado, lo descartamos
    const newStatus = diagnosis.status === 'discarded' ? null : 'discarded';
    updatedDiagnoses[index].status = newStatus;
    
    setDynamicDiagnoses(updatedDiagnoses);
    
    // Notificar al componente padre
    if (onDiagnosisStatusChange) {
      onDiagnosisStatusChange({
        diagnosisName: diagnosis.term,
        diagnosisId: diagnosis.id,
        status: newStatus,
        probability: diagnosis.probability,
        severity: diagnosis.severity,
        specialty: diagnosis.specialty,
        bodySystem: diagnosis.bodySystem,
        description: diagnosis.description,
        supportingFactors: diagnosis.supportingFactors,
        contradictingFactors: diagnosis.contradictingFactors,
        timestamp: new Date().toISOString()
      });
    }
  };

  // FUNCIÓN MODIFICADA: Manejar el click del botón "Ver Detalle"
const handleVerDetalle = (diagnosis) => {
  // Preparar los datos del diagnóstico con información adicional para el análisis
  const diagnosisWithDetails = {
    ...diagnosis,
    // Agregar explícitamente el nombre del diagnóstico
    diagnosticName: diagnosis.term,
    selectedSymptoms: selectedSymptoms,
    analysisData: {
      totalSymptoms: selectedSymptoms.length,
      matchingSymptoms: diagnosis.supportingFactors.length,
      contradictions: diagnosis.contradictingFactors.length,
      calculatedProbability: diagnosis.probability,
      analysisTimestamp: new Date().toISOString()
    }
  };

  // Pasar el diagnóstico al componente padre
  if (onDiagnosisSelect) {
    onDiagnosisSelect(diagnosisWithDetails);
  }
};

  // Clases CSS para UI
  const getButtonClass = (symptom, level) => {
    const currentLevel = symptom.severity === "mild" ? 1 : 
                        symptom.severity === "moderate" ? 2 : 
                        symptom.severity === "severe" ? 3 : 0;
    
    return currentLevel === level ? 
      (level === 1 ? "bg-success text-white" : level === 2 ? "bg-warning text-white" : "bg-danger text-white") :
      (level === 1 ? "border-success text-success" : level === 2 ? "border-warning text-warning" : "border-danger text-danger");
  };

  const getBadgeText = (severity) => {
    if (!severity || severity === "present") return "Presente";
    return severityMap[severity] || "Presente";
  };
  
  const getBadgeClass = (severity) => {
    if (!severity) return badgeColorMap.default;
    return badgeColorMap[severity] || badgeColorMap.present;
  };

  return (
    <div className="container-fluid">
      {/* Panel de Síntomas */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title">Síntomas del Paciente</h5>
            <button className="btn btn-outline-secondary" onClick={() => setShowPatientHistory(!showPatientHistory)}>
              <FileText size={18} className="me-1" /> Historial
            </button>
          </div>
          
          {/* Historial del Paciente */}
          {showPatientHistory && (
            <div className="card mb-3 border-info">
              <div className="card-header bg-info text-white d-flex justify-content-between">
                <span>Historial de consultas previas</span>
                <button className="btn-close btn-close-white" onClick={() => setShowPatientHistory(false)}></button>
              </div>
              <div className="card-body p-2">
                {mockPatientHistory.map((consultation, idx) => (
                  <div key={idx} className="mb-2">
                    <h6>{consultation.consultationDate}</h6>
                    <div className="ms-2">
                      {consultation.symptoms.map(symptom => (
                        <div key={symptom.id} className="d-flex justify-content-between mb-1">
                          <span>
                            {symptom.term}
                            <span className={`badge ${getBadgeClass(symptom.severity)} ms-1`}>
                              {getBadgeText(symptom.severity)}
                            </span>
                          </span>
                          <button className="btn btn-sm btn-outline-primary"
                            onClick={() => importFromHistory([symptom])}
                            disabled={selectedSymptoms.some(s => s.id === symptom.id)}>
                            <Plus size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button className="btn btn-sm btn-outline-primary mt-1"
                      onClick={() => importFromHistory(consultation.symptoms)}>
                      Importar todos
                    </button>
                    <hr className="my-2" />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Alertas */}
          {contradictions.length > 0 && (
            <div className="alert alert-warning p-2 mb-2">
              <div className="d-flex align-items-center mb-1">
                <AlertCircle size={16} className="me-1" />
                <strong>Inconsistencias:</strong>
              </div>
              <ul className="mb-0 ps-3">
                {contradictions.map((c, idx) => (<li key={idx}>{c}</li>))}
              </ul>
            </div>
          )}
          
          {/* Búsqueda */}
          <div className="mb-2">
            <div className="input-group">
              <input type="text" className="form-control" placeholder="Buscar síntoma..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              <button className="btn btn-primary" 
                onClick={() => searchTerm && addSymptom({ id: `custom-${Date.now()}`, term: searchTerm })}>
                Añadir
              </button>
            </div>
            
            {/* Resultados */}
            {searchResults.length > 0 && (
              <div className="list-group mt-1 small">
                {searchResults.slice(0, 5).map(result => (
                  <button key={result.id} className="list-group-item list-group-item-action py-1 px-2 d-flex justify-content-between"
                    onClick={() => addSymptom(result)}>
                    <span>{result.term}</span>
                    <Plus size={14} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Síntomas seleccionados (Pills) */}
          <div className="mb-2">
            {selectedSymptoms.map(symptom => (
              <span key={symptom.id} className={`badge bg-light text-dark me-1 mb-1 p-1 ${symptom.imported ? 'border border-info' : ''}`}>
                {symptom.term}
                <span className={`badge ${getBadgeClass(symptom.severity)} ms-1`}>{getBadgeText(symptom.severity)}</span>
                {symptom.imported && <span className="text-info ms-1">(i)</span>}
                <button onClick={() => removeSymptom(symptom.id)} className="btn-close ms-1" style={{fontSize: '0.6rem'}}></button>
              </span>
            ))}
          </div>
          
          {/* Sugerencias */}
          {selectedSymptoms.length > 0 && getRelatedSymptoms().length > 0 && relatedSymptomsVisible && (
            <div className="card mt-2 border-info">
              <div className="card-header bg-info bg-opacity-10 py-1 px-2 d-flex justify-content-between">
                <h6 className="mb-0 small">Síntomas Relacionados</h6>
                <button className="btn-close" style={{fontSize: '0.6rem'}} onClick={() => setRelatedSymptomsVisible(false)}></button>
              </div>
              <div className="card-body p-1">
                <div className="row g-1">
                  {getRelatedSymptoms().map(symptom => (
                    <div key={symptom.id} className="col-md-6">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>{symptom.term}</small>
                        <button className="btn btn-sm btn-outline-primary py-0 px-1" onClick={() => addSymptom(symptom)}>
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Síntomas detallados */}
          {selectedSymptoms.length > 0 && (
            <div className="mt-3">
              <h6>Síntomas registrados ({selectedSymptoms.length})</h6>
              <div className="symptoms-vertical">
                {selectedSymptoms.map(symptom => (
                  <div key={symptom.id} className="card mb-1 border">
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="fw-bold">
                          {symptom.term}
                          {symptom.imported && <span className="badge bg-info bg-opacity-10 text-info ms-1">Importado</span>}
                        </span>
                        <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeSymptom(symptom.id)}>
                          <X size={12} />
                        </button>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="btn-group btn-group-sm">
                          <button className={`btn btn-sm ${getButtonClass(symptom, 1)}`}
                            onClick={() => updateSeverity(symptom.id, 1)} title="Leve">L</button>
                          <button className={`btn btn-sm ${getButtonClass(symptom, 2)}`}
                            onClick={() => updateSeverity(symptom.id, 2)} title="Moderada">M</button>
                          <button className={`btn btn-sm ${getButtonClass(symptom, 3)}`}
                            onClick={() => updateSeverity(symptom.id, 3)} title="Severa">S</button>
                        </div>
                        
                        <div className="d-flex align-items-center">
                          <span className="badge bg-info me-1">{severityMap[symptom.severity] || 'Presente'}</span>
                          <button className="btn btn-sm btn-outline-secondary py-0 px-1" 
                            onClick={() => openNotesModal(symptom)}>
                            <Edit size={12} />
                          </button>
                        </div>
                      </div>
                      
                      {symptom.notes && (
                        <div className="mt-1">
                          <small className="text-muted fst-italic">
                            {symptom.notes.length > 30 ? symptom.notes.substring(0, 30) + '...' : symptom.notes}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Panel de Diagnósticos */}
      {dynamicDiagnoses.length > 0 && (
        <div className="card mt-3 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="d-flex align-items-center">
                <Activity size={18} className="me-1" />
                <strong>Diagnósticos</strong>
                <span className="badge bg-primary ms-1">
                  {filteredDiagnoses.length || dynamicDiagnoses.length}
                </span>
              </h5>
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={16} className="me-1" />
                Filtros
              </button>
            </div>
            
            {/* Panel de filtros */}
            {showFilters && (
              <div className="card mb-3 border-secondary">
                <div className="card-header bg-secondary text-white d-flex justify-content-between">
                  <span>Filtros de Diagnóstico</span>
                  <button 
                    className="btn-close btn-close-white btn-sm" 
                    onClick={() => setShowFilters(false)}
                  ></button>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Especialidad</label>
                    <select 
                      className="form-select"
                      value={filters.specialty || ''}
                      onChange={(e) => setFilters({...filters, specialty: e.target.value || null})}
                    >
                      <option value="">Todas</option>
                      {specialties.map((specialty, index) => (
                        <option key={index} value={specialty}>{specialty}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Sistema Corporal</label>
                    <select 
                      className="form-select"
                      value={filters.bodySystem || ''}
                      onChange={(e) => setFilters({...filters, bodySystem: e.target.value || null})}
                    >
                      <option value="">Todos</option>
                      {bodySystems.map((system, index) => (
                        <option key={index} value={system}>{system}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Probabilidad Mínima</label>
                    <div className="input-group">
                      <input 
                        type="number" 
                        className="form-control" 
                        min="0" 
                        max="100"
                        value={filters.minimumProbability}
                        onChange={(e) => setFilters({...filters, minimumProbability: parseInt(e.target.value) || 0})}
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="severityCheck"
                        checked={filters.showOnlySevere}
                        onChange={(e) => setFilters({...filters, showOnlySevere: e.target.checked})}
                      />
                      <label className="form-check-label" htmlFor="severityCheck">
                        Solo alta severidad
                      </label>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setFilters({
                        specialty: null,
                        bodySystem: null,
                        minimumProbability: 0,
                        showOnlySevere: false
                      })}
                    >
                      Limpiar filtros
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-2">
              {(filteredDiagnoses.length > 0 ? filteredDiagnoses : dynamicDiagnoses).map((diagnosis, index) => (
                <div key={diagnosis.id} className="mb-2">
                  <div className={`card border-start border-3 ${
                    diagnosis.status === 'confirmed' ? 'border-success' : 
                    diagnosis.status === 'discarded' ? 'border-danger' : 'border-primary'
                  }`}>
                    <div className="card-header bg-light py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">
                            #{index + 1} {diagnosis.term} 
                            <span className={`badge ms-1 ${diagnosis.severity === "alta" ? "bg-danger" : 
                                          diagnosis.severity === "media" ? "bg-warning" : "bg-success"}`}>
                              {diagnosis.severity.charAt(0).toUpperCase() + diagnosis.severity.slice(1)}
                            </span>
                            {diagnosis.status === 'confirmed' && 
                              <span className="badge bg-success ms-1">Confirmado</span>
                            }
                            {diagnosis.status === 'discarded' && 
                              <span className="badge bg-danger ms-1">Descartado</span>
                            }
                          </h6>
                          <small className="text-muted">{diagnosis.specialty.join(', ')} • {diagnosis.bodySystem}</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="text-end me-2">
                            <div className="fs-5 fw-bold text-primary">{diagnosis.probability}%</div>
                          </div>
                          <button className="btn btn-sm btn-outline-secondary"
                            onClick={() => toggleDiagnosisExpand(index)}>
                            {expandedDiagnoses.includes(index) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>
                      <div className="progress mt-1" style={{height: '3px'}}>
                        <div className={`progress-bar ${diagnosis.probability >= 80 ? "bg-danger" : 
                                      diagnosis.probability >= 60 ? "bg-warning" : 
                                      diagnosis.probability >= 40 ? "bg-info" : "bg-success"}`}
                          style={{width: `${diagnosis.probability}%`}} />
                      </div>
                    </div>
                    
                    {expandedDiagnoses.includes(index) && (
                      <div className="card-body p-2">
                        <p className="small">{diagnosis.description}</p>
                        
                        <div className="row g-2">
                          {diagnosis.supportingFactors.length > 0 && (
  <div className="col-md-6">
    <h6 className="text-success small">
      <Check size={14} className="me-1" />
      Factores de Apoyo ({diagnosis.supportingFactors.length})
                    </h6>
                    <ul className="list-unstyled small">
                      {diagnosis.supportingFactors.map((factor, idx) => (
                        <li key={idx} className="mb-1">
                          <span className={`badge me-1 ${
                            factor.importance === 'high' ? 'bg-success' : 
                            factor.importance === 'medium' ? 'bg-warning' : 'bg-info'
                          }`}>
                            {factor.importance === 'high' ? 'Alto' : 
                             factor.importance === 'medium' ? 'Medio' : 'Bajo'}
                          </span>
                          <strong>{factor.factor}:</strong> {factor.status}
                          {factor.severity && <span className="text-muted"> ({severityMap[factor.severity]})</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {diagnosis.contradictingFactors.length > 0 && (
                  <div className="col-md-6">
                    <h6 className="text-warning small">
                      <AlertCircle size={14} className="me-1" />
                      Factores en Contra ({diagnosis.contradictingFactors.length})
                    </h6>
                    <ul className="list-unstyled small">
                      {diagnosis.contradictingFactors.map((factor, idx) => (
                        <li key={idx} className="mb-1">
                          <span className={`badge me-1 ${
                            factor.importance === 'high' ? 'bg-danger' : 
                            factor.importance === 'medium' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {factor.importance === 'high' ? 'Alto' : 
                             factor.importance === 'medium' ? 'Medio' : 'Bajo'}
                          </span>
                          <strong>{factor.factor}:</strong> {factor.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
                        
                        {/* Botones de acción */}
              <div className="d-flex justify-content-between mt-3">
                <div>
                  <button 
                    className={`btn btn-sm me-2 ${
                      diagnosis.status === 'confirmed' ? 'btn-success' : 'btn-outline-success'
                    }`}
                    onClick={() => toggleConfirmDiagnosis(index)}
                  >
                    <Check size={14} className="me-1" />
                    {diagnosis.status === 'confirmed' ? 'Confirmado' : 'Confirmar'}
                  </button>
                  
                  <button 
                    className={`btn btn-sm me-2 ${
                      diagnosis.status === 'discarded' ? 'btn-danger' : 'btn-outline-danger'
                    }`}
                    onClick={() => toggleDiscardDiagnosis(index)}
                  >
                    <X size={14} className="me-1" />
                    {diagnosis.status === 'discarded' ? 'Descartado' : 'Descartar'}
                  </button>
                </div>
                
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleVerDetalle(diagnosis)}
                >
                  <FileText size={14} className="me-1" />
                  Ver Detalle
                </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notas */}
{showNotesModal && (
<div className="modal show" style={{display: 'block'}} tabIndex="-1">
  <div className="modal-dialog">
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">Notas para: {currentSymptom?.term}</h5>
        <button type="button" className="btn-close" onClick={() => setShowNotesModal(false)}></button>
      </div>
      <div className="modal-body">
        <textarea 
          className="form-control" 
          rows="4" 
          value={symptomNotes}
          onChange={(e) => setSymptomNotes(e.target.value)}
          placeholder="Agregar observaciones o notas sobre este síntoma..."
        />
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={() => setShowNotesModal(false)}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary" onClick={saveNotes}>
          Guardar Notas
        </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}